import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

import Link from 'components/Shared/Common/Link';
import Button from 'components/Shared/Common/Button';
import { PopConfirmDeletion } from 'components/Shared/Common/PopConfirm';
import ToggleSound from 'components/Shared/Common/SoundButton/ToggleSound';

import {
    StyledExpandableSectionListItemLink,
    StyledExpandableSectionListItemActions,
    StyledExpandableSectionListItemBadge,
} from 'components/Shared/style';

const FeedItem = (props) => {
    const {
        onRemove,
        onEdit,
        unreadCount = 0,
        label,
        href,
        iconRender,
        isCurrent,
        intl,
        isMuted = false,
        onMute = () => {}
    } = props;

    const translations = useMemo(() => ({
        soundOn: intl.formatMessage({ id: 'menu.dropdown.sound.multicast.on' }),
        confirmDeletionTitle: intl.formatMessage({ id: 'feed.actions.confirmDelete' }, { name: label }),
    }), [intl, label]);

    const handleRemove = useCallback((e) => {
        onRemove?.();
        return e.preventDefault();
    }, [onRemove]);

    const handleEdit = useCallback((e) => {
        onEdit?.();
        return e.preventDefault();
    }, [onEdit]);

    const nodes = useMemo(() => {
        const list = [];

        if (isCurrent) {
            list.push(
                <StyledExpandableSectionListItemLink
                    key="item"
                    className={classNames(
                        'item',
                        { unread: unreadCount }
                    )}
                >
                    <Link href={null} className="wrapper-content">
                        {isMuted ? (
                            <ToggleSound
                                enabled={!isMuted}
                                buttonClassName="small space-around"
                                onClick={onMute}
                                iconOff="BellOffCustom"
                                soundOn={translations.soundOn}
                            />
                        ) : iconRender}
                        <div className="name">
                            <div className="content">
                                {label}
                            </div>
                        </div>
                    </Link>
                </StyledExpandableSectionListItemLink>
            );
        } else {
            list.push((
                <StyledExpandableSectionListItemLink
                    key="itemLink"
                    className={classNames(
                        'item',
                        { unread: unreadCount }
                    )}
                >
                    <Link href={href} className="wrapper-content">
                        {isMuted ? (
                            <ToggleSound
                                enabled={!isMuted}
                                buttonClassName="small space-around"
                                onClick={onMute}
                                iconOff="BellOffCustom"
                                soundOn={translations.soundOn}
                            />
                        ) : iconRender}
                        <div className="name">
                            <div className="content">
                                {label}
                            </div>
                        </div>
                    </Link>
                </StyledExpandableSectionListItemLink>
            ));
        }

        if (typeof onRemove === 'function') {
            list.push(
                <StyledExpandableSectionListItemActions
                    className="s-list-actions" 
                    key="delete"
                >
                    <div className="hovered">
                        <PopConfirmDeletion
                            title={translations.confirmDeletionTitle}
                            onConfirm={handleRemove}
                        />
                    </div>
                </StyledExpandableSectionListItemActions>
            );
        }

        if (typeof onEdit === 'function') {
            list.push(
                <StyledExpandableSectionListItemActions
                    className="s-list-actions"
                    onClick={handleEdit}
                    key="onRemove"
                >
                    <div className="hovered">
                        <Button
                            iconName="Settings"
                            color="lighten"
                            isTransparent
                            size="xs"
                        />
                    </div>
                </StyledExpandableSectionListItemActions>
            );
        }

        if (unreadCount) {
            list.push(
                <StyledExpandableSectionListItemBadge 
                    key="onNotification" 
                    count={unreadCount}
                    className="ui-expandable-list-item-badge"
                    size="small" 
                    overflowCount={9}
                />
            );
        }

        return list;
    }, [
        unreadCount,
        onEdit,
        handleEdit,
        handleRemove,
        onRemove,
        translations,
        onMute,
        isMuted,
        href,
        label,
        isCurrent,
        iconRender
    ]);

    return nodes;
};

FeedItem.propTypes = {
    isMuted: PropTypes.bool,
    onMute: PropTypes.func,
    onRemove: PropTypes.func,
    intl: PropTypes.any.isRequired,
    onEdit: PropTypes.func,
    label: PropTypes.string.isRequired,
    unreadCount: PropTypes.number,
    href: PropTypes.string.isRequired
};

export default memo(injectIntl(FeedItem));
